/**
 * Payment Processing Types and Interfaces
 * For manual payment logging and approval workflow
 */

export interface ManualPayment {
  id?: string;
  landlordId: string;
  propertyId: string;
  tenantId: string;
  amount: number;
  paymentDate: Date | any;
  method: 'check' | 'cash' | 'ach' | 'other';
  reference?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'posted' | 'rejected';
  daysLate: number;
  createdAt: any;
  updatedAt: any;
  createdBy: string;
  approvedBy?: string;
  approvedAt?: any;
  rejectionReason?: string;
  notificationSentAt?: any;
}

export interface LateFee {
  id?: string;
  landlordId: string;
  propertyId: string;
  tenantId: string;
  amount: number;
  reason: 'auto' | 'manual' | 'nsfFee' | 'leaseViolation' | 'other';
  reasonDetails?: string;
  status: 'pending' | 'waived' | 'paid' | 'applied';
  createdAt: any;
  updatedAt: any;
  createdBy: string;
  waivedBy?: string;
  waiverReason?: string;
  waivedAt?: any;
  appliedToPaymentId?: string;
  appliedAt?: any;
}

export interface LateFeePolicy {
  landlordId: string;
  propertyId?: string;
  autoLateFees: boolean;
  gracePeriodDays: number;
  lateFeAmount: number;
  lateFeeType: 'fixed' | 'percentage';
  maxLateFee?: number;
  applyMultipleTimes: boolean;
  notifyTenantOfLateFee: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface PaymentApprovalNotification {
  id?: string;
  landlordId: string;
  tenantId: string;
  paymentId: string;
  amount: number;
  paymentDate: Date | any;
  daysLate: number;
  method: string;
  status: 'pending' | 'read' | 'approved' | 'rejected';
  approvedAt?: any;
  rejectedAt?: any;
  rejectionReason?: string;
  createdAt: any;
  expiresAt: any;
}

export interface DashboardLateFeeStats {
  pendingLateFees: number;
  totalLateFeeAmount: number;
  waivedLateFees: number;
  appliedLateFees: number;
  tenantsWithLateFees: number;
}

export const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'ach', label: 'ACH Transfer' },
  { value: 'other', label: 'Other' },
];

export const LATE_FEE_REASONS = [
  { value: 'auto', label: 'Automatic Late Fee' },
  { value: 'manual', label: 'Manual Entry' },
  { value: 'nsfFee', label: 'NSF (Non-Sufficient Funds)' },
  { value: 'leaseViolation', label: 'Lease Violation' },
  { value: 'other', label: 'Other' },
];

export const calculateDaysLate = (paymentDate: Date | any, dueDate: Date | any): number => {
  const payment = paymentDate instanceof Date ? paymentDate : new Date(paymentDate.seconds * 1000);
  const due = dueDate instanceof Date ? dueDate : new Date(dueDate.seconds * 1000);
  
  const diffTime = Math.abs(payment.getTime() - due.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return payment > due ? diffDays : 0;
};

export const isPaymentOnTime = (paymentDate: Date | any, dueDate: Date | any): boolean => {
  const payment = paymentDate instanceof Date ? paymentDate : new Date(paymentDate.seconds * 1000);
  const due = dueDate instanceof Date ? dueDate : new Date(dueDate.seconds * 1000);
  
  return payment <= due;
};
